import React, { useId, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@mui/material';

type ServiceMetadata = { service?: unknown; services?: unknown; constraints?: Record<string, unknown> | null };

/** Single-line cells retain their full value on hover and keyboard focus. */
export function LibraryTextCell({ value }: { value: unknown }) {
  const text = typeof value === 'string' && value.trim() ? value : 'Not specified';
  return (
    <Tooltip title={text} describeChild>
      <Box component="span" tabIndex={0} sx={{ display: 'block', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {text}
      </Box>
    </Tooltip>
  );
}

export function stringValues(value: unknown): string[] {
  const values = typeof value === 'string' ? [value] : Array.isArray(value) ? value : [];
  return Array.from(new Set(values.filter((item): item is string => typeof item === 'string' && !!item.trim()).map(item => item.trim())));
}

type TagOwner = { projectId: string; internalId: string | number; testId: string; name: string };

/** Preserve the API list exactly; preview ordering must not rewrite stored metadata. */
export function originalTags(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((tag): tag is string => typeof tag === 'string') : [];
}

export function descriptiveTagPreview(tags: readonly string[], limit = 2): string[] {
  const priority = (tag: string) => {
    const value = tag.trim().toLowerCase();
    if (['negative', 'positive'].includes(value)) return 0;
    if (['boundary', 'auth', 'validation'].includes(value)) return 1;
    return ['synthetic', 'functional'].includes(value) ? 3 : 2;
  };
  const ranked = tags.map((value, index) => ({ value, index }))
    .sort((a, b) => priority(a.value) - priority(b.value) || a.index - b.index);
  return Array.from(new Set(ranked.map(item => item.value))).slice(0, limit);
}

export function LibraryTagsCell({ projectId, row }: {
  projectId: string;
  row: { id: number | string; test_id: string; name: string; tags: unknown };
}) {
  const tags = originalTags(row.tags);
  const owner = { projectId, internalId: row.id, testId: row.test_id, name: row.name };
  // A changed record, project or payload closes an existing dialog immediately.
  return <LibraryValuesCell key={JSON.stringify([owner, tags])} values={tags}
    previewValues={descriptiveTagPreview(tags)} owner={owner} title="All tags" />;
}

/** Runtime mappings are metadata, not inferred logical ownership or global scope. */
export function libraryServices(row: ServiceMetadata) {
  // Preserve explicitly returned associations without guessing from names or globs.
  const supplied = Array.isArray(row.services) ? row.services.map(value => typeof value === 'string' ? value : value?.service) : [];
  return { primary: stringValues([...stringValues(row.service), ...supplied]), runtime: stringValues(row.constraints?.runtime_services) };
}

export function LibraryValuesCell({ values, title, limit = 2, details, previewValues, owner }: {
  values: string[];
  title: string;
  limit?: number;
  details?: React.ReactNode;
  previewValues?: string[];
  owner?: TagOwner;
}) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const preview = previewValues ?? values.slice(0, limit);
  const extra = Math.max(0, values.length - preview.length);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0, overflow: 'hidden', flexWrap: 'nowrap' }}>
      {values.length ? preview.map((value, index) => (
        <Tooltip key={index} title={value} describeChild>
          <Chip label={value} tabIndex={0} size="small" variant="outlined" sx={{ minWidth: 0, maxWidth: '100%', flexShrink: 1, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }} />
        </Tooltip>
      )) : <Typography noWrap variant="body2">Not specified</Typography>}
      {(extra > 0 || details) && (
        <Button size="small" aria-label={`Show ${title.toLowerCase()}${owner ? ` for ${owner.testId}` : ''}`} aria-haspopup="dialog" aria-expanded={open} onClick={event => { event.stopPropagation(); setOpen(true); }} sx={{ minWidth: 36, px: 0.5, flexShrink: 0 }}>
          {extra > 0 ? `+${extra}` : 'Details'}
        </Button>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth aria-labelledby={titleId}>
        <DialogTitle id={titleId}>{title}</DialogTitle>
        <DialogContent sx={{ overflowWrap: 'anywhere' }}>
          {owner && <Box sx={{ mb: 2 }}>
            <Typography variant="body2">Test ID: {owner.testId}</Typography>
            <Typography variant="body2">Name: {owner.name}</Typography>
            <Typography variant="caption">Project: {owner.projectId} · Record: {owner.internalId}</Typography>
          </Box>}
          <Box component="ul" sx={{ m: 0, pl: 3 }}>{values.map((value, index) => <li key={index}>{value}</li>)}</Box>
          {details}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export function LibraryServiceCell({ row }: { row: ServiceMetadata }) {
  const { primary, runtime } = libraryServices(row);
  const details = runtime.length ? (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2">Configured runtime labels</Typography>
      <Typography variant="body2">Explicit mapping metadata; does not imply additional logical ownership.</Typography>
      <Box component="ul" sx={{ pl: 3 }}>{runtime.map(value => <li key={value}>{value}</li>)}</Box>
    </Box>
  ) : undefined;
  return <LibraryValuesCell values={primary} title="Service details" limit={1} details={details} />;
}
